import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UploadTaskStateEnum, isPendingUploadTaskState } from '../enums/upload-task-state.enum';
import { UploadTaskStatusInterface } from '../interfaces/upload-task-status.interface';

@Injectable({
  providedIn: 'root',
})
export class UploadTaskTrackerService {
  private readonly tasksSubject = new BehaviorSubject<Record<string, UploadTaskStatusInterface>>({});

  public readonly tasks$: Observable<UploadTaskStatusInterface[]> = this.tasksSubject.asObservable().pipe(
    map((tasks) => Object.values(tasks))
  );

  public readonly hasPendingTasks$: Observable<boolean> = this.tasks$.pipe(
    map((tasks) => tasks.some((task) => isPendingUploadTaskState(task.state)))
  );

  public hasPendingTasks(): boolean {
    return Object.values(this.tasksSubject.value).some((task) => isPendingUploadTaskState(task.state));
  }

  public hasPendingTasksInFlow(flowPrefix: string): boolean {
    return Object.values(this.tasksSubject.value).some((task) => {
      return task.flow?.startsWith(flowPrefix) && isPendingUploadTaskState(task.state);
    });
  }

  public clearTasksByFlow(flowPrefix: string): void {
    const nextTasks = Object.fromEntries(
      Object.entries(this.tasksSubject.value).filter(([, task]) => !task.flow?.startsWith(flowPrefix))
    );
    this.tasksSubject.next(nextTasks);
  }

  public startTask(params: {
    clientUploadId: string;
    fileName: string;
    mimeType: string;
    sizeBeforeBytes: number;
    flow: string;
  }): void {
    const now = Date.now();
    const task: UploadTaskStatusInterface = {
      clientUploadId: params.clientUploadId,
      fileName: params.fileName,
      mimeType: params.mimeType,
      sizeBeforeBytes: params.sizeBeforeBytes,
      sizeAfterBytes: params.sizeBeforeBytes,
      state: UploadTaskStateEnum.PENDING_PREPROCESS,
      flow: params.flow,
      createdAt: now,
      updatedAt: now,
    };

    this.setTask(task);
  }

  public markPendingUpload(clientUploadId: string, sizeAfterBytes: number): void {
    this.patchTask(clientUploadId, {
      sizeAfterBytes,
      state: UploadTaskStateEnum.PENDING_UPLOAD,
      error: undefined,
    });
  }

  public markUploaded(clientUploadId: string, sizeAfterBytes?: number): void {
    this.patchTask(clientUploadId, {
      sizeAfterBytes,
      state: UploadTaskStateEnum.UPLOADED,
      error: undefined,
    });
  }

  public markFailed(clientUploadId: string, error?: unknown): void {
    this.patchTask(clientUploadId, {
      state: UploadTaskStateEnum.FAILED,
      error: this.normalizeError(error),
    });
  }

  private patchTask(clientUploadId: string, patch: {
    sizeAfterBytes?: number;
    state: UploadTaskStateEnum;
    error?: string;
    preprocessReason?: string;
  }): void {
    const tasks = this.tasksSubject.value;
    const existing = tasks[clientUploadId];
    if (!existing) {
      return;
    }

    this.setTask({
      ...existing,
      sizeAfterBytes: patch.sizeAfterBytes ?? existing.sizeAfterBytes,
      state: patch.state,
      error: patch.error,
      preprocessReason: patch.preprocessReason ?? existing.preprocessReason,
      updatedAt: Date.now(),
    });
  }

  public setPreprocessReason(clientUploadId: string, preprocessReason: string): void {
    const tasks = this.tasksSubject.value;
    const existing = tasks[clientUploadId];
    if (!existing) {
      return;
    }

    this.setTask({
      ...existing,
      preprocessReason,
      updatedAt: Date.now(),
    });
  }

  private setTask(task: UploadTaskStatusInterface): void {
    this.tasksSubject.next({
      ...this.tasksSubject.value,
      [task.clientUploadId]: task,
    });
  }

  private normalizeError(error: unknown): string {
    if (!error) {
      return 'Unknown upload error';
    }

    if (typeof error === 'string') {
      return error;
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'Upload failed';
  }
}
